import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { register } from '../../utils/api';
import Seo from '../common/Seo';

interface RegisterFormData {
  email: string;
  password: string;
  confirmPassword: string;
  username: string;
  club: string;
}

const Register = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<RegisterFormData>({
    email: '',
    password: '',
    confirmPassword: '',
    username: '',
    club: ''
  });
  const [error, setError] = useState<string>('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwörter stimmen nicht überein!');
      return;
    }

    try {
      const userData = {
        name: formData.username,
        email: formData.email,
        password: formData.password,
        club: formData.club
      };

      const response = await register(userData);
      
      if (response.success) {
        navigate('/login');
      } else {
        setError(response.message || 'Registrierung fehlgeschlagen');
      }
    } catch (err) {
      console.error('Registration error:', err);
      setError('Ein Fehler ist aufgetreten');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col items-center pt-16">
      <Seo title="Registrieren | SportyReplay" path="/register" noindex />

      <div className="text-3xl font-bold mb-8">
        <span className="text-white">Sporty</span>
        <span className="text-orange-500">Replay</span>
      </div>
      
      <div className="w-full max-w-md bg-white rounded-lg shadow-xl p-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">Registrierung</h2>
        {error && (
          <div className="mb-4 p-2 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-700 mb-2" htmlFor="username">
              Benutzername
            </label>
            <input
              type="text"
              id="username"
              name="username"
              className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={formData.username}
              onChange={handleChange}
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 mb-2" htmlFor="club">
              Verein <span className="text-gray-400 text-sm">(optional)</span>
            </label>
            <input
              type="text"
              id="club"
              name="club"
              className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-400"
              value={formData.club}
              onChange={handleChange}
              placeholder="z.B. dein Verein"
              disabled={formData.club === 'Vereinslos'}
            />
            <label className="flex items-center gap-2 mt-2 cursor-pointer select-none">
              <input
                type="checkbox"
                className="w-4 h-4 accent-orange-500"
                checked={formData.club === 'Vereinslos'}
                onChange={e => setFormData({ ...formData, club: e.target.checked ? 'Vereinslos' : '' })}
              />
              <span className="text-sm text-gray-600">Ich gehöre keinem Verein an (Vereinslos)</span>
            </label>
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 mb-2" htmlFor="email">
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 mb-2" htmlFor="password">
              Passwort
            </label>
            <input
              type="password"
              id="password"
              name="password"
              className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={formData.password}
              onChange={handleChange}
              minLength={8}
              required
            />
            <p className="mt-1 text-xs text-gray-500">Mindestens 8 Zeichen</p>
          </div>
          <div className="mb-6">
            <label className="block text-gray-700 mb-2" htmlFor="confirmPassword">
              Passwort bestätigen
            </label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
            />
          </div>
          <button
            type="submit"
            className="w-full bg-orange-500 text-white py-3 px-4 rounded hover:bg-orange-600 transition duration-200"
          >
            Registrieren
          </button>
        </form>
      </div>
    </div>
  );
};

export default Register;